import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ACCOUNT_SUBTYPES,
  ACCOUNT_TYPES,
  NORMAL_BALANCE_MAP,
  type AccountType,
  type BankTransaction,
  type ExistingEntryMatch,
  type MatchCandidate,
  type MatchStatus,
} from '@accounting/shared';
import { computeRuleSplits } from '@accounting/shared';
import {
  useBankAccounts,
  useBankRules,
  useBankTransactions,
  useApplyRulesToCompany,
  useBulkMatchCandidates,
  useBulkMatchedOffsets,
  useExistingEntryMatches,
  useReclassifyBankTransaction,
  useReclassifyBankTransactionsBulk,
  type MatchedOffsetInfo,
  useExcludeBankTransaction,
  useMatchAsIntercompany,
  useMatchBankTransaction,
  useMatchCandidates,
  useMatchToExisting,
  useOpenPayables,
  useSettlePayables,
  useSettleReceivable,
  useMatchTransferPair,
  useSuggestCategories,
  useUnexcludeBankTransaction,
  useUnmatchBankTransaction,
} from '../../hooks/useBank';
import { useAccounts, useCreateAccount } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import {
  ExistingEntrySuggestionCard,
  useVisibleExistingEntryMatches,
} from '../../components/bank/ExistingEntrySuggestion';
import { useCompanies } from '../../hooks/useCompanies';
import { useContacts } from '../../hooks/useContacts';
import { useClasses, useLocations } from '../../hooks/useDimensions';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency, humanizeLabel } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const Route = createFileRoute('/_auth/bank/transactions')({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    bankAccountId?: string;
    status?: MatchStatus | 'all';
    q?: string;
    page?: number;
    pageSize?: PageSize;
  } => {
    const status = search.status as string;
    const page = Number(search.page);
    const pageSize = Number(search.pageSize);
    const q = typeof search.q === 'string' ? search.q.slice(0, 200) : undefined;
    return {
      bankAccountId: typeof search.bankAccountId === 'string' ? search.bankAccountId : undefined,
      status: ['unmatched', 'matched', 'excluded', 'all'].includes(status)
        ? (status as MatchStatus | 'all')
        : undefined,
      q: q && q.trim() ? q : undefined,
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : undefined,
      pageSize: (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSize)
        ? (pageSize as PageSize)
        : undefined,
    };
  },
  component: BankTransactionsPage,
});

const TABS: { value: MatchStatus | 'all'; label: string }[] = [
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'matched', label: 'Matched' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'all', label: 'All' },
];

function BankTransactionsPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: bankAccountsData } = useBankAccounts(companyId);
  const bankAccounts = bankAccountsData?.bankAccounts ?? [];

  const search = Route.useSearch();
  const tab = search.status ?? 'unmatched';
  const bankAccountId = search.bankAccountId ?? '';
  const q = search.q ?? '';
  const page = search.page ?? 1;
  const pageSize: PageSize = search.pageSize ?? 50;

  // Single helper so every control updates URL search params (drives back/forward + sharable links).
  const setSearch = (next: Partial<{ status: MatchStatus | 'all'; bankAccountId: string; q: string; page: number; pageSize: PageSize }>) =>
    void navigate({
      search: (prev) => {
        const merged = { ...prev, ...next };
        // Resetting filters always resets to page 1 (existing offset is stale for new filter set).
        if (
          next.status !== undefined ||
          next.bankAccountId !== undefined ||
          next.q !== undefined ||
          next.pageSize !== undefined
        ) {
          merged.page = 1;
        }
        // Empty bankAccountId / q means "no filter" — keep URL clean.
        if (merged.bankAccountId === '') delete merged.bankAccountId;
        if (merged.q === '') delete merged.q;
        if (merged.page === 1) delete merged.page;
        if (merged.pageSize === 50) delete merged.pageSize;
        return merged;
      },
    });

  // Local input state so typing doesn't trigger a fetch on every keystroke;
  // commit (debounce 350ms) before sending to URL/server.
  const [qInput, setQInput] = useState<string>(q);
  useEffect(() => setQInput(q), [q]); // sync if URL changes (back/forward)
  useEffect(() => {
    if (qInput === q) return;
    const t = setTimeout(() => setSearch({ q: qInput.trim() }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const offset = (page - 1) * pageSize;
  const { data, isLoading, isFetching } = useBankTransactions(companyId, {
    matchStatus: tab === 'all' ? undefined : tab,
    bankAccountId: bankAccountId || undefined,
    q: q || undefined,
    // Pending rows come along greyed out so a fresh sync is visible; the
    // server refuses every action on them until they settle.
    includePending: true,
    limit: pageSize,
    offset,
  });
  const txns = data?.transactions ?? [];
  const pendingOnPage = txns.filter((t) => t.pending).length;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const { data: accounts = [] } = useAccounts(companyId);
  const acctById = new Map(accounts.map((a) => [a.id, a]));
  const suggest = useSuggestCategories(companyId);
  const applyRules = useApplyRulesToCompany(companyId);
  const [applyRulesMsg, setApplyRulesMsg] = useState<string | null>(null);
  const handleApplyRules = (): void => {
    if (applyRules.isPending) return;
    setApplyRulesMsg(null);
    applyRules.mutate(bankAccountId || null, {
      onSuccess: (r) => {
        const parts: string[] = [];
        parts.push(`Examined ${r.txnsExamined} txn${r.txnsExamined === 1 ? '' : 's'}`);
        if (r.autoPosted) parts.push(`${r.autoPosted} auto-posted`);
        if (r.suggested) parts.push(`${r.suggested} suggested`);
        if (r.suppressedByCeiling) parts.push(`${r.suppressedByCeiling} suppressed (over ceiling)`);
        if (r.rulesMatched === 0) parts.push('no rules matched');
        setApplyRulesMsg(parts.join(' · '));
      },
      onError: (e) => setApplyRulesMsg(`Failed: ${(e as Error).message}`),
    });
  };

  // Bulk-fetch candidates for unmatched rows on this page. One round-trip vs N.
  const unmatchedIds = useMemo(
    () => txns.filter((t) => t.matchStatus === 'unmatched' && !t.pending).map((t) => t.id),
    [txns],
  );
  const { data: candidatesByTxId = {} } = useBulkMatchCandidates(companyId, unmatchedIds);

  // Proactive "you already posted this" suggestions for the same rows — the
  // feed says so up front instead of waiting for the user to open the drawer
  // and go looking for a duplicate they don't know exists.
  const { data: suggestionsByTxId = {} } = useExistingEntryMatches(companyId, unmatchedIds);

  // Bulk-fetch the offset account for matched/reconciled rows on this page so
  // the Matched tab can show where each transaction was posted at a glance.
  const matchedIds = useMemo(
    () =>
      txns
        .filter((t) => t.matchStatus === 'matched' || t.matchStatus === 'reconciled')
        .map((t) => t.id),
    [txns],
  );
  const { data: offsetsByTxId = {} } = useBulkMatchedOffsets(companyId, matchedIds);

  // Selection — keyed by tx ID. Cleared when the user changes tab/filter/page
  // so they don't accidentally operate on rows they can no longer see.
  const [selected, setSelected] = useState<Record<string, true>>({});
  const selectedIds = Object.keys(selected);

  // What "bulk" means depends on the active tab:
  //   unmatched → bulk Add (post AI-suggested category as a new JE per row)
  //   matched   → bulk Unmatch (void each JE, return tx to unmatched)
  //   excluded  → bulk Unexclude (flip back to unmatched so rules/AI can retry)
  //   all       → no bulk (mixed states)
  type BulkMode = 'add' | 'unmatch' | 'unexclude' | null;
  const bulkMode: BulkMode =
    tab === 'unmatched' ? 'add'
    : tab === 'matched' ? 'unmatch'
    : tab === 'excluded' ? 'unexclude'
    : null;

  const bulkEligible = useMemo(() => {
    if (bulkMode === 'add') {
      // Select All ticks every unmatched row so the user can bulk Exclude
      // them in one click. Bulk Add silently skips rows without an AI
      // suggestion (rule rows are auto-handled when the user runs rules).
      return txns.filter((t) => t.matchStatus === 'unmatched' && !t.pending);
    }
    if (bulkMode === 'unmatch') return txns.filter((t) => t.matchStatus === 'matched');
    if (bulkMode === 'unexclude') return txns.filter((t) => t.matchStatus === 'excluded');
    return [];
  }, [txns, bulkMode]);

  const allEligibleSelected =
    bulkEligible.length > 0 && bulkEligible.every((t) => selected[t.id]);

  const toggleAllEligible = () =>
    setSelected((prev) => {
      if (allEligibleSelected) {
        const next = { ...prev };
        for (const t of bulkEligible) delete next[t.id];
        return next;
      }
      const next = { ...prev };
      for (const t of bulkEligible) next[t.id] = true;
      return next;
    });

  const match = useMatchBankTransaction(companyId);
  const unmatchMut = useUnmatchBankTransaction(companyId);
  const unexcludeMut = useUnexcludeBankTransaction(companyId);
  const excludeMut = useExcludeBankTransaction(companyId);

  // Bulk reclassify (matched tab only). A whole season of payroll drafts landing
  // on the wrong account is the normal case, not the exception — doing it a row
  // at a time is what stopped it being fixed.
  const { data: pageAccounts = [] } = useAccounts(companyId);
  const bulkReclass = useReclassifyBankTransactionsBulk(companyId);
  const [bulkReclassOpen, setBulkReclassOpen] = useState(false);
  const [bulkReclassAccount, setBulkReclassAccount] = useState('');
  const [bulkReclassNote, setBulkReclassNote] = useState<string | null>(null);

  const runBulkReclassify = () => {
    if (selectedIds.length === 0 || !bulkReclassAccount) return;
    setBulkReclassNote(null);
    bulkReclass.mutate(
      { transactionIds: selectedIds.slice(), accountId: bulkReclassAccount },
      {
        onSuccess: (res) => {
          // Per-row failures don't abort the sweep, so say plainly how many
          // moved and why the rest didn't rather than reporting a flat success.
          if (res.failed.length === 0) {
            setBulkReclassOpen(false);
            setBulkReclassAccount('');
            setSelected({});
            setBulkReclassNote(`Reclassified ${res.succeeded.length}.`);
            return;
          }
          const next = { ...selected };
          for (const id of res.succeeded) delete next[id];
          setSelected(next);
          setBulkReclassNote(
            `Reclassified ${res.succeeded.length}. ${res.failed.length} could not move — ${res.failed[0]!.reason}`,
          );
        },
        onError: (e) => {
          setBulkReclassNote(
            e instanceof ApiError
              ? (e.body as { error?: string } | null)?.error ?? e.message
              : (e as Error).message,
          );
        },
      },
    );
  };

  const bulkBusy =
    match.isPending ||
    unmatchMut.isPending ||
    unexcludeMut.isPending ||
    excludeMut.isPending;

  // Bulk exclude is offered alongside Add on the unmatched tab — handy for
  // pruning noise (own-account transfers, duplicates, pending/zero rows).
  // Operates on EVERY selected unmatched row regardless of whether it has an
  // AI suggestion. No reason prompt — by user request.
  const runBulkExclude = async () => {
    if (selectedIds.length === 0) return;
    const excludable = selectedIds
      .map((id) => txns.find((t) => t.id === id))
      .filter((t): t is BankTransaction => !!t && t.matchStatus === 'unmatched');
    if (excludable.length === 0) return;
    if (
      !confirm(
        `Exclude ${excludable.length} transaction(s)? They will move to the Excluded tab and stop appearing in suggestions and rules.`,
      )
    ) {
      return;
    }
    for (const tx of excludable) {
      try {
        await excludeMut.mutateAsync({ transactionId: tx.id });
        setSelected((p) => {
          const n = { ...p };
          delete n[tx.id];
          return n;
        });
      } catch {
        // Stop on first failure so the user can investigate.
        break;
      }
    }
  };

  const runBulk = async () => {
    if (selectedIds.length === 0 || !bulkMode) return;
    const label =
      bulkMode === 'add' ? `Add ${selectedIds.length} entries using their suggested categories? Each will create a new journal entry.`
      : bulkMode === 'unmatch' ? `Unmatch ${selectedIds.length} transactions? Their JEs will be voided.`
      : `Un-exclude ${selectedIds.length} transactions? They will return to the unmatched list.`;
    if (!confirm(label)) return;
    const ids = selectedIds.slice();
    for (const id of ids) {
      const tx = txns.find((t) => t.id === id);
      if (!tx) continue;
      try {
        if (bulkMode === 'add') {
          if (!tx.aiSuggestedAccountId) continue;
          await match.mutateAsync({
            transactionId: tx.id,
            input: { accountId: tx.aiSuggestedAccountId, memo: tx.description },
          });
        } else if (bulkMode === 'unmatch') {
          await unmatchMut.mutateAsync(tx.id);
        } else if (bulkMode === 'unexclude') {
          await unexcludeMut.mutateAsync(tx.id);
        }
        setSelected((p) => { const n = { ...p }; delete n[id]; return n; });
      } catch {
        // Stop on first failure; the user can investigate that row and re-try.
        break;
      }
    }
  };

  // Clear selection when the active tab changes (or any other filter) so the
  // bulk bar can't act on stale ids.
  useEffect(() => setSelected({}), [tab, bankAccountId, q, page, pageSize]);

  const [matchingTx, setMatchingTx] = useState<BankTransaction | null>(null);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <Link to="/bank" style={{ fontSize: 14 }}>← Bank accounts</Link>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          marginBottom: 16,
        }}
      >
        <h1 style={{ fontSize: 24, margin: 0 }}>Transactions</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn-secondary"
            disabled={suggest.isPending}
            title="Suggest categories for unmatched transactions based on how you categorized similar ones before"
            onClick={() => suggest.mutate(bankAccountId || undefined)}
          >
            {suggest.isPending ? 'Suggesting…' : 'Suggest categories'}
          </button>
          <button
            type="button"
            disabled={applyRules.isPending}
            title={
              bankAccountId
                ? 'Run this company\'s active rules against unmatched transactions on the selected bank account. Auto-post rules below their ceiling will post; suggest-only rules will tag transactions with the rule badge.'
                : 'Run this company\'s active rules against ALL currently-unmatched transactions in every bank account. Auto-post rules below their ceiling will post; suggest-only rules will tag transactions for one-click acceptance.'
            }
            onClick={handleApplyRules}
            style={{
              padding: '6px 14px',
              border: '1px solid var(--accent)',
              borderRadius: 6,
              background: 'var(--accent-soft, color-mix(in srgb, var(--accent) 12%, transparent))',
              color: 'var(--accent)',
              cursor: applyRules.isPending ? 'progress' : 'pointer',
              fontWeight: 500,
            }}
          >
            {applyRules.isPending ? 'Applying rules…' : 'Apply rules'}
          </button>
          <Link to="/bank/rules" className="btn-secondary" style={{ textDecoration: 'none' }}>
            Manage rules
          </Link>
        </div>
      </div>
      {suggest.isSuccess && (
        <p className="muted" style={{ marginTop: -8, marginBottom: 16, fontSize: 13 }}>
          Suggested {suggest.data.updated} from past activity
          {suggest.data.aiUpdated > 0 ? ` and ${suggest.data.aiUpdated} via AI` : ''}.
        </p>
      )}
      {applyRulesMsg && (
        <p className="muted" style={{ marginTop: -8, marginBottom: 16, fontSize: 13 }}>
          Apply rules: {applyRulesMsg}
        </p>
      )}

      {pendingOnPage > 0 && (
        <p className="muted" style={{ marginTop: -8, marginBottom: 12, fontSize: 13 }}>
          {pendingOnPage} transaction{pendingOnPage === 1 ? ' is' : 's are'} still pending at the bank (greyed out).
          They can be matched once they settle; the bank issues a new id at settlement.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.value}
            className={tab === t.value ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setSearch({ status: t.value })}
          >
            {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 14, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            Description:&nbsp;
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="e.g. CK # or ACH"
              title="Filter by substring of description or merchant. Case-insensitive."
              style={{ width: 180 }}
            />
            {qInput && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setQInput('')}
                title="Clear description filter"
                style={{ padding: '2px 8px', fontSize: 12 }}
              >
                ×
              </button>
            )}
          </label>
          <label style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            Bank account:&nbsp;
            <select
              value={bankAccountId}
              onChange={(e) => setSearch({ bankAccountId: e.target.value })}
            >
              <option value="">All accounts</option>
              {bankAccounts.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.accountName}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            Per page:&nbsp;
            <select
              value={pageSize}
              onChange={(e) => setSearch({ pageSize: Number(e.target.value) as PageSize })}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {selectedIds.length > 0 && bulkMode && (
        <div
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            marginBottom: 12,
            background: 'var(--accent-soft, var(--bg-surface))',
          }}
        >
          <span style={{ fontWeight: 500 }}>{selectedIds.length} selected</span>
          <button
            type="button"
            className={bulkMode === 'add' ? 'btn-primary' : 'btn-secondary'}
            disabled={bulkBusy}
            onClick={runBulk}
            title={
              bulkMode === 'add'
                ? 'Create a new journal entry for each selected row using its suggested category.'
                : bulkMode === 'unmatch'
                  ? 'Void the linked journal entry for each selected row (or just unlink, for match-existing rows).'
                  : 'Return each selected row to the unmatched feed so rules and suggestions can re-evaluate it.'
            }
          >
            {bulkBusy
              ? 'Working…'
              : bulkMode === 'add'
                ? `Add ${selectedIds.length} using suggested categories`
                : bulkMode === 'unmatch'
                  ? `Unmatch ${selectedIds.length}`
                  : `Un-exclude ${selectedIds.length}`}
          </button>
          {bulkMode === 'add' && (
            <button
              type="button"
              className="btn-secondary"
              disabled={bulkBusy}
              onClick={runBulkExclude}
              title="Move each selected row to the Excluded tab so it stops appearing in suggestions and rules."
            >
              Exclude {selectedIds.length} selected
            </button>
          )}
          {bulkMode === 'unmatch' && !bulkReclassOpen && (
            <button
              type="button"
              className="btn-secondary"
              disabled={bulkBusy || bulkReclass.isPending}
              onClick={() => {
                setBulkReclassNote(null);
                setBulkReclassAccount('');
                setBulkReclassOpen(true);
              }}
              title="Move every selected row's offset line to one account. The bank line and the amounts are untouched."
            >
              Reclassify {selectedIds.length}…
            </button>
          )}
          {bulkMode === 'unmatch' && bulkReclassOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 260 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <AccountPicker
                  accounts={pageAccounts}
                  value={bulkReclassAccount}
                  onChange={(v) => setBulkReclassAccount(v)}
                  predicate={(a) => a.isActive && a.allowDirectPosting}
                />
              </div>
              <button
                type="button"
                className="btn-primary"
                disabled={!bulkReclassAccount || bulkReclass.isPending}
                onClick={runBulkReclassify}
              >
                {bulkReclass.isPending ? 'Moving…' : `Move ${selectedIds.length}`}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setBulkReclassOpen(false);
                  setBulkReclassNote(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSelected({})}
          >
            Clear selection
          </button>
        </div>
      )}

      {bulkReclassNote && (
        <div className="card" style={{ padding: 10, marginBottom: 12, fontSize: 13 }}>
          {bulkReclassNote}
        </div>
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && txns.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No {tab === 'all' ? '' : tab} transactions.
          </p>
        </div>
      )}

      {txns.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  {bulkEligible.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleAllEligible}
                      title={
                        bulkMode === 'add'
                          ? allEligibleSelected
                            ? `Deselect all ${bulkEligible.length} suggested rows on this page`
                            : `Select all ${bulkEligible.length} rows with a suggested category on this page`
                          : bulkMode === 'unmatch'
                            ? allEligibleSelected
                              ? `Deselect all ${bulkEligible.length} matched rows on this page`
                              : `Select all ${bulkEligible.length} matched rows to bulk-unmatch`
                            : allEligibleSelected
                              ? `Deselect all ${bulkEligible.length} excluded rows on this page`
                              : `Select all ${bulkEligible.length} excluded rows to un-exclude`
                      }
                    />
                  )}
                </th>
                <th style={{ width: 110 }}>Date</th>
                <th className="right" style={{ width: 120 }}>Amount</th>
                <th>Description</th>
                <th style={{ width: 180 }}>Offset</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((tx) => {
                const reviewing = matchingTx?.id === tx.id;
                return (
                  <Fragment key={tx.id}>
                    <TransactionRow
                      tx={tx}
                      companyId={companyId}
                      acctById={acctById}
                      candidates={candidatesByTxId[tx.id] ?? []}
                      existingEntryMatches={suggestionsByTxId[tx.id] ?? []}
                      matchedOffset={offsetsByTxId[tx.id] ?? null}
                      selected={!!selected[tx.id]}
                      expanded={reviewing}
                      onToggleSelected={() =>
                        setSelected((p) => {
                          const n = { ...p };
                          if (n[tx.id]) delete n[tx.id];
                          else n[tx.id] = true;
                          return n;
                        })
                      }
                      onReview={() => setMatchingTx(reviewing ? null : tx)}
                    />
                    {/* The review panel expands UNDER its own row rather than
                      * sliding in from the side, so the transaction you are
                      * categorising stays visible directly above the form. */}
                    {reviewing && (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            padding: 0,
                            background: 'var(--bg-surface-2)',
                            borderLeft: '3px solid var(--primary)',
                          }}
                        >
                          <MatchDrawer
                            tx={tx}
                            companyId={companyId}
                            onClose={() => setMatchingTx(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
              fontSize: 14,
              color: 'var(--text-muted)',
            }}
          >
            <div>
              {total === 0
                ? 'No results'
                : `Showing ${offset + 1}–${Math.min(offset + txns.length, total)} of ${total}`}
              {isFetching && ' · loading…'}
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                className="btn-secondary"
                disabled={page <= 1}
                onClick={() => setSearch({ page: 1 })}
                title="First page"
              >
                «
              </button>
              <button
                className="btn-secondary"
                disabled={page <= 1}
                onClick={() => setSearch({ page: page - 1 })}
              >
                ‹ Prev
              </button>
              <span style={{ padding: '0 8px' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setSearch({ page: page + 1 })}
              >
                Next ›
              </button>
              <button
                className="btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setSearch({ page: totalPages })}
                title="Last page"
              >
                »
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

function TransactionRow({
  tx,
  companyId,
  acctById,
  candidates,
  existingEntryMatches,
  matchedOffset,
  selected,
  expanded,
  onToggleSelected,
  onReview,
}: {
  tx: BankTransaction;
  companyId: string;
  acctById: Map<string, { code: string; name: string }>;
  candidates: MatchCandidate[];
  existingEntryMatches: ExistingEntryMatch[];
  matchedOffset: MatchedOffsetInfo | null;
  selected: boolean;
  /** True while this row's review panel is expanded beneath it. */
  expanded: boolean;
  onToggleSelected: () => void;
  onReview: () => void;
}) {
  const { data: rules = [] } = useBankRules(companyId, { includeInactive: true });
  const { data: allAccounts = [] } = useAccounts(companyId);
  const unmatch = useUnmatchBankTransaction(companyId);
  const exclude = useExcludeBankTransaction(companyId);
  const unexclude = useUnexcludeBankTransaction(companyId);
  const match = useMatchBankTransaction(companyId);
  const settleReceivableTop = useSettleReceivable(companyId);
  const settlePayablesTop = useSettlePayables(companyId);
  const matchExisting = useMatchToExisting(companyId);
  const matchPair = useMatchTransferPair(companyId);
  const reclassify = useReclassifyBankTransaction(companyId);
  const navigate = useNavigate();

  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [reclassifyTarget, setReclassifyTarget] = useState<string>('');
  const [reclassifyError, setReclassifyError] = useState<string | null>(null);
  const isSplitMatch = matchedOffset?.isSplit ?? false;

  const amt = parseFloat(tx.amount);
  const isOut = amt > 0;
  const suggestedRule = tx.matchedByRuleId
    ? rules.find((r) => r.id === tx.matchedByRuleId)
    : null;
  const suggestedAcct =
    !suggestedRule && tx.aiSuggestedAccountId ? acctById.get(tx.aiSuggestedAccountId) : undefined;
  const confidencePct = tx.aiConfidence ? Math.round(parseFloat(tx.aiConfidence) * 100) : null;
  const topMatch = candidates[0];
  const moreCandidates = Math.max(0, candidates.length - 1);

  // "You already posted this" — an entry keyed by hand that looks like this
  // transaction. Shown unprompted, so the user can wave it off; dismissals are
  // a per-user UI preference and live in localStorage, not the ledger.
  const { visible: visibleSuggestions, dismiss: dismissSuggestion } =
    useVisibleExistingEntryMatches(tx.id, existingEntryMatches);
  const suggestion =
    tx.matchStatus === 'unmatched' ? visibleSuggestions[0] : undefined;

  const handleLinkSuggestion = () => {
    if (!suggestion) return;
    matchExisting.mutate({
      transactionId: tx.id,
      journalEntryId: suggestion.journalEntryId,
    });
  };

  const isUnmatched = tx.matchStatus === 'unmatched';
  const isMatchedRow = tx.matchStatus === 'matched';
  const isExcludedRow = tx.matchStatus === 'excluded';
  // Per-row selection eligibility: any unmatched row can be selected (Add
  // silently skips rows without a suggestion; bulk Exclude works on all of
  // them — handy for pruning noise the AI didn't categorize). Matched and
  // excluded rows are selectable for their own bulk actions.
  const canSelect = isUnmatched || isMatchedRow || isExcludedRow;

  const handleAdd = () => {
    if (!tx.aiSuggestedAccountId) return;
    match.mutate({
      transactionId: tx.id,
      input: { accountId: tx.aiSuggestedAccountId, memo: tx.description },
    });
  };

  const handleMatchTop = () => {
    if (!topMatch) return;
    if (topMatch.kind === 'existing' && topMatch.journalEntryId) {
      matchExisting.mutate({
        transactionId: tx.id,
        journalEntryId: topMatch.journalEntryId,
      });
    } else if (topMatch.kind === 'undeposited' && topMatch.offsetAccountId) {
      match.mutate({
        transactionId: tx.id,
        input: { accountId: topMatch.offsetAccountId, memo: tx.description },
      });
    } else if (topMatch.kind === 'transfer_pair' && topMatch.pairBankTransactionId) {
      matchPair.mutate({
        transactionId: tx.id,
        pairBankTransactionId: topMatch.pairBankTransactionId,
      });
    } else if (topMatch.kind === 'receivable' && topMatch.receivableLineId) {
      settleReceivableTop.mutate({
        transactionId: tx.id,
        receivableLineId: topMatch.receivableLineId,
      });
    } else if (topMatch.kind === 'payable' && topMatch.payableLineId) {
      settlePayablesTop.mutate({
        transactionId: tx.id,
        journalLineIds: [topMatch.payableLineId],
      });
    }
  };

  // Open the rules page with prefill state in URL search params so the form
  // pre-populates from this transaction (description fragment, amount, target
  // account). User tweaks before saving.
  const handleCreateRule = () => {
    const merchant = tx.merchantName?.trim();
    const matchValue = merchant && merchant.length >= 3
      ? merchant
      : tx.description.split(/\s+/).slice(0, 2).join(' ');
    void navigate({
      to: '/bank/rules',
      search: {
        fromTx: tx.id,
        prefillName: `Auto-categorize ${matchValue}`,
        prefillMatchValue: matchValue,
        prefillAmount: Math.abs(amt).toFixed(2),
        prefillBankAccountId: tx.bankAccountId,
        prefillTargetAccountId: tx.aiSuggestedAccountId ?? '',
      },
    });
  };

  const inFlight = match.isPending || matchExisting.isPending;

  // Pending at the bank: visible so the sync is evidently alive, inert so it
  // cannot be booked twice (it settles under a new id). No checkbox, no
  // suggestions, no actions — the server would refuse them anyway.
  if (tx.pending) {
    return (
      <tr style={{ opacity: 0.55 }} title="Pending at the bank — actions unlock when it settles">
        <td />
        <td>{formatAccountingDate(tx.date)}</td>
        <td className="right" style={{ fontWeight: 500 }}>
          {isOut ? '' : '+'}
          {formatCurrency(Math.abs(amt).toFixed(2))}
        </td>
        <td>
          <div>{tx.description}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {tx.merchantName ?? '—'}
            <span className="tag" style={{ marginLeft: 8 }}>pending</span>
          </div>
        </td>
        <td colSpan={3} className="muted" style={{ fontSize: 12 }}>
          Settles under a new id; nothing to do yet.
        </td>
      </tr>
    );
  }

  return (
    // While the panel is open below, tie the two together visually — otherwise
    // an expanded form reads as belonging to whichever row you last looked at.
    <tr
      style={
        expanded
          ? { background: 'var(--bg-surface-2)', boxShadow: 'inset 3px 0 0 var(--primary)' }
          : undefined
      }
    >
      <td>
        {canSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            title="Select for bulk action"
          />
        )}
      </td>
      <td>{formatAccountingDate(tx.date)}</td>
      <td
        className="right"
        style={{
          color: isOut ? 'var(--text-default)' : 'var(--ok)',
          fontWeight: 500,
        }}
      >
        {isOut ? '' : '+'}
        {formatCurrency(Math.abs(amt).toFixed(2))}
      </td>
      <td>
        <div>{tx.description}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {tx.merchantName ?? '—'}
          {suggestedRule && isUnmatched && (
            <span className="tag" style={{ marginLeft: 8 }}>
              Rule: {suggestedRule.name}
            </span>
          )}
          {!suggestedRule && suggestedAcct && isUnmatched && !topMatch && (
            <span
              className="tag"
              style={{ marginLeft: 8 }}
              title={tx.aiReasoning ?? 'Suggested from past transactions'}
            >
              Suggested: {suggestedAcct.code} {suggestedAcct.name}
              {confidencePct !== null ? ` (${confidencePct}%)` : ''}
            </span>
          )}
        </div>
        {/* Proactive duplicate warning: an entry the owner already posted that
            looks like this transaction, offered right here so they never post a
            second one. */}
        {suggestion && (
          <ExistingEntrySuggestionCard
            match={suggestion}
            otherCount={visibleSuggestions.length - 1}
            isOut={isOut}
            isLinking={inFlight}
            onLink={handleLinkSuggestion}
            onDismiss={() => dismissSuggestion(suggestion.journalEntryId)}
            onReview={onReview}
          />
        )}
        {/* Inline match preview so the user can verify which existing JE before
            clicking Match. Always show date + dollar amount prominently — many
            transactions in a feed have identical amounts and only the date and
            memo disambiguate them. Skipped when the suggestion card above is
            already showing the SAME entry with a confidence and a reason; a
            different candidate still gets its own preview. */}
        {isUnmatched &&
          topMatch &&
          !(suggestion && topMatch.journalEntryId === suggestion.journalEntryId) && (
          <div
            className="card"
            style={{
              marginTop: 6,
              padding: '6px 10px',
              fontSize: 12,
              background: 'var(--bg-surface)',
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            <div>
              <strong>
                {topMatch.kind === 'transfer_pair'
                  ? 'Transfer pair:'
                  : topMatch.kind === 'undeposited'
                    ? 'Undeposited funds:'
                    : topMatch.kind === 'receivable'
                      ? 'Open receivable:'
                      : topMatch.kind === 'payable'
                        ? 'Unpaid payroll item:'
                        : 'Existing entry:'}
              </strong>{' '}
              {topMatch.kind === 'existing' && topMatch.journalEntryId ? (
                <Link to="/journal/$id" params={{ id: topMatch.journalEntryId }}>
                  {topMatch.entryNumber ?? topMatch.label}
                </Link>
              ) : (
                <span>{topMatch.label}</span>
              )}
              {topMatch.date && (
                <span> · <strong>{formatAccountingDate(topMatch.date)}</strong></span>
              )}
              <span>
                {' · '}
                <strong>{formatCurrency(Math.abs(parseFloat(topMatch.amount)).toFixed(2))}</strong>
              </span>
            </div>
            {topMatch.topOffsetAccountCode && (
              <div className="muted">
                {isOut ? 'DR' : 'CR'} {topMatch.topOffsetAccountCode} {topMatch.topOffsetAccountName}
              </div>
            )}
            {topMatch.entryMemo && (
              <div style={{ marginTop: 2, fontStyle: 'italic' }}>"{topMatch.entryMemo}"</div>
            )}
          </div>
        )}
      </td>
      <td style={{ fontSize: 13 }}>
        {matchedOffset ? (
          <span title={`${matchedOffset.accountCode} · ${matchedOffset.accountName}${matchedOffset.isSplit ? ' (split — largest line shown)' : ''}`}>
            <span className="muted">{matchedOffset.accountCode}</span>{' '}
            {matchedOffset.accountName}
            {matchedOffset.isSplit && (
              <span className="tag" style={{ marginLeft: 6 }}>split</span>
            )}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        {tx.matchStatus === 'matched' && <span className="tag ok">matched</span>}
        {isUnmatched && <span className="tag warn">unmatched</span>}
        {tx.matchStatus === 'reconciled' && <span className="tag">reconciled</span>}
        {tx.matchStatus === 'excluded' && (
          <span className="tag bad" title={tx.excludedReason ?? ''}>excluded</span>
        )}
      </td>
      <td>
        {isUnmatched && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
            {/* When a candidate exists: Match is primary. Add still appears if the AI
                suggested a category (lets the user override the match). */}
            {topMatch && (
              <button
                className="btn-primary"
                onClick={handleMatchTop}
                disabled={inFlight}
                title={
                  topMatch.kind === 'existing'
                    ? `Link this transaction to ${topMatch.entryNumber} (no new entry created).`
                    : `Clear ${topMatch.label}`
                }
              >
                {inFlight ? 'Matching…' : 'Match'}
              </button>
            )}
            {moreCandidates > 0 && (
              <button
                type="button"
                className="btn-secondary"
                onClick={onReview}
                title={`${moreCandidates} other entries with the same amount`}
              >
                Find other matches ({moreCandidates})
              </button>
            )}
            {suggestedAcct && (
              <button
                className={topMatch ? 'btn-secondary' : 'btn-primary'}
                onClick={handleAdd}
                disabled={inFlight}
                title={`Create a new entry posting to ${suggestedAcct.code} ${suggestedAcct.name}.`}
              >
                {inFlight ? 'Adding…' : topMatch ? 'Add new instead' : 'Add'}
              </button>
            )}
            {!topMatch && !suggestedAcct && (
              <button className="btn-primary" onClick={onReview}>
                {expanded ? 'Close' : 'Review'}
              </button>
            )}
            {(topMatch || suggestedAcct) && (
              <button
                type="button"
                className="btn-secondary"
                onClick={onReview}
                title={expanded ? 'Close the review panel' : 'Open the full review panel'}
              >
                {expanded ? 'Close' : 'More…'}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCreateRule}
              title="Create a rule that auto-categorizes future transactions like this one."
            >
              Create rule
            </button>
            <button
              className="btn-secondary"
              onClick={() => exclude.mutate({ transactionId: tx.id })}
              title="Move this transaction to the Excluded tab. It will stop appearing in suggestions and rules."
            >
              Exclude
            </button>
          </div>
        )}
        {isMatchedRow && !reclassifyOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              className="btn-secondary"
              style={{ width: '100%' }}
              disabled={isSplitMatch}
              title={
                isSplitMatch
                  ? 'Split match — unmatch and re-post to change categories.'
                  : 'Re-point this transaction to a different expense account. Safe even on reconciled rows — bank side stays intact.'
              }
              onClick={() => {
                setReclassifyError(null);
                setReclassifyTarget('');
                setReclassifyOpen(true);
              }}
            >
              Reclassify
            </button>
            <button
              className="btn-secondary"
              style={{ width: '100%' }}
              onClick={() => {
                if (confirm('Unmatch this transaction? (A categorized entry is voided; a matched existing entry is just unlinked.)')) {
                  unmatch.mutate(tx.id);
                }
              }}
              disabled={unmatch.isPending}
            >
              Unmatch
            </button>
          </div>
        )}
        {isMatchedRow && reclassifyOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <AccountPicker
              accounts={allAccounts}
              value={reclassifyTarget}
              onChange={(v) => setReclassifyTarget(v)}
              // Not expense-only. A draft that settles an accrued liability
              // (payroll tax, uncleared wage cheques) is the case this most
              // needs to reach, and it lives on the balance sheet.
              predicate={(a) => a.isActive && a.allowDirectPosting}
            />
            {reclassifyError && (
              <div style={{ color: 'var(--bad)', fontSize: 12 }}>{reclassifyError}</div>
            )}
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                className="btn-primary"
                style={{ flex: 1 }}
                disabled={!reclassifyTarget || reclassify.isPending}
                onClick={() => {
                  setReclassifyError(null);
                  reclassify.mutate(
                    { transactionId: tx.id, accountId: reclassifyTarget },
                    {
                      onSuccess: () => {
                        setReclassifyOpen(false);
                        setReclassifyTarget('');
                      },
                      onError: (e) => {
                        const msg =
                          e instanceof ApiError
                            ? (e.body as { error?: string } | null)?.error ?? e.message
                            : (e as Error).message;
                        setReclassifyError(msg);
                      },
                    },
                  );
                }}
              >
                {reclassify.isPending ? 'Saving…' : 'Apply'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setReclassifyOpen(false);
                  setReclassifyError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {isExcludedRow && (
          <button
            className="btn-secondary"
            style={{ width: '100%' }}
            onClick={() => {
              if (confirm('Return this transaction to the unmatched list? Rules and suggestions will re-evaluate it.')) {
                unexclude.mutate(tx.id);
              }
            }}
            disabled={unexclude.isPending}
            title={tx.excludedReason ? `Excluded reason: ${tx.excludedReason}` : undefined}
          >
            Un-exclude
          </button>
        )}
      </td>
    </tr>
  );
}

/** GL-account picker with an inline "+ New" form so you can create an account
 * without leaving the match drawer. On create it selects the new account. */
function AccountSelect({
  companyId,
  accounts,
  value,
  onChange,
  id,
  required,
}: {
  companyId: string;
  accounts: import('@accounting/shared').Account[];
  value: string;
  onChange: (id: string) => void;
  id?: string;
  required?: boolean;
}) {
  const create = useCreateAccount(companyId);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('expense');
  const [accountSubtype, setAccountSubtype] = useState('');

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not create account'
      : create.error?.message;

  const submit = () => {
    if (!name.trim()) return;
    create.mutate(
      {
        name: name.trim(),
        code: code.trim() || undefined,
        accountType,
        accountSubtype: accountSubtype || null,
        normalBalance: NORMAL_BALANCE_MAP[accountType],
        allowDirectPosting: true,
        isSystem: false,
      },
      {
        onSuccess: (a) => {
          onChange(a.id);
          setCreating(false);
          setName('');
          setCode('');
          setAccountSubtype('');
        },
      },
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <AccountPicker
            id={id}
            accounts={accounts}
            value={value}
            onChange={onChange}
            predicate={(a) => a.isActive && a.allowDirectPosting}
            required={required}
          />
        </div>
        <button type="button" className="btn-secondary" onClick={() => setCreating((v) => !v)}>
          {creating ? '×' : '+ New'}
        </button>
      </div>
      {creating && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 6, display: 'grid', gap: 6 }}>
          <input placeholder="Account name *" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              value={accountType}
              onChange={(e) => {
                setAccountType(e.target.value as AccountType);
                setAccountSubtype('');
              }}
              style={{ flex: 1 }}
            >
              {ACCOUNT_TYPES.map((t) => (
                <option key={t} value={t}>{humanizeLabel(t)}</option>
              ))}
            </select>
            <select value={accountSubtype} onChange={(e) => setAccountSubtype(e.target.value)} style={{ flex: 1 }}>
              <option value="">— subtype —</option>
              {(ACCOUNT_SUBTYPES[accountType] ?? []).map((s) => (
                <option key={s} value={s}>{humanizeLabel(s)}</option>
              ))}
            </select>
          </div>
          <input
            placeholder="Code (blank = auto)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ fontFamily: 'ui-monospace, monospace' }}
          />
          {errorMessage && <div className="field-error" style={{ fontSize: 12 }}>{errorMessage}</div>}
          <button type="button" className="btn-primary" disabled={!name.trim() || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create & select'}
          </button>
        </div>
      )}
    </div>
  );
}

function MatchDrawer({
  tx,
  companyId,
  onClose,
}: {
  tx: BankTransaction;
  companyId: string;
  onClose: () => void;
}) {
  const match = useMatchBankTransaction(companyId);
  const matchIc = useMatchAsIntercompany(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: contacts = [] } = useContacts(companyId);
  const { data: locations = [] } = useLocations(companyId);
  const { data: classes = [] } = useClasses(companyId);
  const { data: rules = [] } = useBankRules(companyId);
  const { data: companies = [] } = useCompanies();
  const { data: candidates = [] } = useMatchCandidates(companyId, tx.id);
  const settleReceivable = useSettleReceivable(companyId);
  const matchExisting = useMatchToExisting(companyId);
  const matchPair = useMatchTransferPair(companyId);

  const [intercompany, setIntercompany] = useState(false);
  const [otherCompanyId, setOtherCompanyId] = useState('');
  const [otherAccountId, setOtherAccountId] = useState('');
  const otherCompanies = companies.filter((c) => c.id !== companyId);
  const company = companies.find((c) => c.id === companyId);
  const { data: otherAccounts = [] } = useAccounts(otherCompanyId || null);
  const otherPostable = otherAccounts.filter((a) => a.isActive && a.allowDirectPosting);

  const suggestedRule = tx.matchedByRuleId
    ? rules.find((r) => r.id === tx.matchedByRuleId)
    : null;
  // A suggested rule with split lines opens the drawer already split the way
  // the rule would post it — same shared arithmetic as the sync job — so
  // accepting the suggestion is one click, not re-keying the lines.
  const ruleLines =
    suggestedRule && (suggestedRule.splits?.length ?? 0) > 0
      ? computeRuleSplits(suggestedRule, tx.amount)
      : null;
  const [splitMode, setSplitMode] = useState(ruleLines !== null);
  const [splits, setSplits] = useState<{ accountId: string; amount: string }[]>(
    ruleLines ?? [{ accountId: '', amount: '' }],
  );

  const [accountId, setAccountId] = useState<string>(
    suggestedRule?.targetAccountId ?? tx.aiSuggestedAccountId ?? '',
  );
  const [contactId, setContactId] = useState<string>(
    suggestedRule?.targetContactId ?? '',
  );
  const [locationId, setLocationId] = useState<string>(
    suggestedRule?.targetLocationId ?? '',
  );
  const [classId, setClassId] = useState<string>(
    suggestedRule?.targetClassId ?? '',
  );
  const [memo, setMemo] = useState<string>(tx.description);

  const amt = Math.abs(parseFloat(tx.amount));
  const isOut = parseFloat(tx.amount) > 0;

  // Tick-box settlement of payroll accruals. Only money OUT can pay a
  // liability, so the list is not even fetched for a deposit.
  const settlePayables = useSettlePayables(companyId);
  const { data: openPayables = [] } = useOpenPayables(companyId, isOut);
  const [pickedPayables, setPickedPayables] = useState<string[]>([]);
  const pickedTotal = openPayables
    .filter((p) => pickedPayables.includes(p.journalLineId))
    .reduce((s, p) => s + parseFloat(p.outstanding), 0);
  // Half a cent of tolerance, matching the server's own check.
  const payablesBalance =
    pickedPayables.length > 0 && Math.abs(pickedTotal - amt) < 0.005;

  // Where a withheld processor fee posts. The company's configured default wins
  // (Settings → Processor fee account); otherwise fall back to guessing from the
  // account name so the feature still works before anyone sets it.
  const feeAccountId =
    company?.processorFeeAccountId ??
    accounts.find(
      (a) =>
        a.isActive &&
        a.allowDirectPosting &&
        /merchant|processing fee|payment fee|stripe/i.test(a.name),
    )?.id ??
    accounts.find((a) => a.isActive && a.allowDirectPosting && /bank fee/i.test(a.name))?.id ??
    '';

  // Inline processor-fee entry for the common case: one deposit, one receivable,
  // one fee. Avoids making the user build a split by hand.
  const [feeOn, setFeeOn] = useState(false);
  const [feeAmount, setFeeAmount] = useState('');
  const [feeAcct, setFeeAcct] = useState(feeAccountId);
  useEffect(() => {
    // The accounts list and the company record load asynchronously; adopt the
    // resolved default once it arrives, but never clobber a manual choice.
    setFeeAcct((prev) => prev || feeAccountId);
  }, [feeAccountId]);

  const feeNum = parseFloat(feeAmount) || 0;
  // The customer paid the fee too — gross is what clears the receivable.
  const grossAmount = amt + feeNum;
  const feeValid = feeNum > 0 && Boolean(feeAcct);

  const splitTotal = splits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  // Split amounts are signed: a negative line posts to the opposite side, which
  // is how a fee withheld from the deposit is entered (A/R 200.00 +
  // merchant fees -6.30 nets to the 193.70 that actually hit the bank).
  const splitValid =
    splitMode &&
    splits.length > 0 &&
    splits.every((r) => r.accountId && (parseFloat(r.amount) || 0) !== 0) &&
    Math.abs(splitTotal - amt) < 0.005;

  const activeErr = intercompany ? matchIc.error : match.error;
  const errorMessage =
    activeErr instanceof ApiError
      ? (activeErr.body as { error?: string } | null)?.error ?? 'Request failed'
      : activeErr?.message;
  const otherName = otherCompanies.find((c) => c.id === otherCompanyId)?.name ?? 'the other company';

  return (
    <div
      // Inline panel, not an overlay: it lives inside the table cell under its
      // own transaction row. Wide-but-capped so the form does not stretch to
      // the full table width on a large screen and become hard to scan.
      style={{ padding: 20, maxWidth: 760 }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18 }}>Review transaction</h2>
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500 }}>{tx.description}</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {formatAccountingDate(tx.date)} ·{' '}
            <span style={{ color: isOut ? 'inherit' : 'var(--ok)' }}>
              {isOut ? 'Out: ' : 'In: '}
              {formatCurrency(amt.toFixed(2))}
            </span>
          </div>
          {tx.merchantName && (
            <div className="muted" style={{ fontSize: 13 }}>
              Merchant: {tx.merchantName}
            </div>
          )}
          {suggestedRule && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <span className="tag">Rule suggestion: {suggestedRule.name}</span>
            </div>
          )}
        </div>

        {candidates.length > 0 && !intercompany && !splitMode && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600 }}>Match — already in your books</div>
            <p className="muted" style={{ marginTop: 4, marginBottom: 8, fontSize: 12 }}>
              This equals an entry you already recorded, money sitting in Undeposited Funds, or an open
              receivable this deposit pays off. Use one of these instead of creating a duplicate.
            </p>
            {candidates.map((c) => (
              <div
                key={c.receivableLineId ?? c.journalEntryId ?? c.offsetAccountId ?? c.label}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid var(--border)' }}
              >
                <div style={{ minWidth: 0 }}>
                  {(c.kind === 'existing' || c.kind === 'receivable') && c.journalEntryId ? (
                    <Link to="/journal/$id" params={{ id: c.journalEntryId }}>{c.label}</Link>
                  ) : (
                    <span>{c.label}</span>
                  )}
                  <div className="muted" style={{ fontSize: 12 }}>
                    {c.date ? `${formatAccountingDate(c.date)} · ` : ''}
                    {formatCurrency(c.amount, true)}
                    {c.topOffsetAccountCode && (
                      <> · {c.topOffsetAccountCode} {c.topOffsetAccountName}</>
                    )}
                  </div>
                  {c.kind === 'receivable' && (
                    // Spell out the accounting, because "Settle" looks like an
                    // ordinary match but deliberately records NO revenue — that
                    // was already recognised when the receivable was accrued.
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      Clears {formatCurrency(c.receivableOutstanding ?? c.amount, true)} of receivable —
                      posts to the bank and A/R only, no new income.
                    </div>
                  )}
                  {c.kind === 'payable' && (
                    // Same reasoning, mirrored: the wage expense was recorded by
                    // the payroll run. Categorising this payment as an expense
                    // would book the same wages twice.
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      Clears {formatCurrency(c.amount, true)} of {c.topOffsetAccountCode}{' '}
                      {c.topOffsetAccountName} — posts to the bank and that liability only, no new expense.
                    </div>
                  )}
                  {c.entryMemo && (
                    <div className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
                      "{c.entryMemo}"
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    matchExisting.isPending ||
                    match.isPending ||
                    matchPair.isPending ||
                    settleReceivable.isPending
                  }
                  onClick={() => {
                    if (c.kind === 'existing' && c.journalEntryId) {
                      matchExisting.mutate(
                        { transactionId: tx.id, journalEntryId: c.journalEntryId },
                        { onSuccess: () => onClose() },
                      );
                    } else if (c.kind === 'undeposited' && c.offsetAccountId) {
                      match.mutate(
                        { transactionId: tx.id, input: { accountId: c.offsetAccountId, memo: memo.trim() || undefined } },
                        { onSuccess: () => onClose() },
                      );
                    } else if (c.kind === 'transfer_pair' && c.pairBankTransactionId) {
                      matchPair.mutate(
                        { transactionId: tx.id, pairBankTransactionId: c.pairBankTransactionId },
                        { onSuccess: () => onClose() },
                      );
                    } else if (c.kind === 'receivable' && c.receivableLineId) {
                      settleReceivable.mutate(
                        { transactionId: tx.id, receivableLineId: c.receivableLineId },
                        { onSuccess: () => onClose() },
                      );
                    } else if (c.kind === 'payable' && c.payableLineId) {
                      settlePayables.mutate(
                        { transactionId: tx.id, journalLineIds: [c.payableLineId] },
                        { onSuccess: () => onClose() },
                      );
                    }
                  }}
                >
                  {c.kind === 'undeposited'
                    ? 'Deposit'
                    : c.kind === 'transfer_pair'
                      ? 'Pair'
                      : c.kind === 'receivable' || c.kind === 'payable'
                        ? 'Settle'
                        : 'Match'}
                </button>
              </div>
            ))}
            {matchExisting.error instanceof ApiError && (
              <div className="field-error" style={{ fontSize: 12, marginTop: 6 }}>
                {(matchExisting.error.body as { error?: string } | null)?.error ?? 'Could not match'}
              </div>
            )}
            {settleReceivable.error instanceof ApiError && (
              <div className="field-error" style={{ fontSize: 12, marginTop: 6 }}>
                {(settleReceivable.error.body as { error?: string } | null)?.error ??
                  'Could not settle the receivable'}
              </div>
            )}
            <p className="muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
              Not one of these? Categorize it as a new entry below.
            </p>
          </div>
        )}

        {/* One draft usually pays several people at once, so a bulk ACH equals
          * no single accrual and can never appear as a candidate above. Tick the
          * paycheques it covers instead; they must add up to the payment. */}
        {isOut && !intercompany && !splitMode && openPayables.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600 }}>Settle payroll items</div>
            <p className="muted" style={{ marginTop: 4, marginBottom: 8, fontSize: 12 }}>
              Tick every paycheque this payment covers. One entry is posted with a line per
              person, so you keep a record of whose cheque cleared — and no new wage expense,
              since the payroll run already booked it.
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {openPayables.map((p) => (
                <label
                  key={p.journalLineId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '6px 0',
                    borderTop: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={pickedPayables.includes(p.journalLineId)}
                    onChange={(e) =>
                      setPickedPayables((prev) =>
                        e.target.checked
                          ? [...prev, p.journalLineId]
                          : prev.filter((id) => id !== p.journalLineId),
                      )
                    }
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {p.partyName ?? p.entryNumber}
                    <span className="muted" style={{ fontSize: 12 }}>
                      {' '}
                      · {formatAccountingDate(p.date)} · {p.accountCode} {p.accountName}
                    </span>
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(p.outstanding)}
                  </span>
                </label>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                marginTop: 10,
                borderTop: '1px solid var(--border)',
                paddingTop: 10,
              }}
            >
              <div style={{ fontSize: 13 }}>
                Selected <strong>{formatCurrency(pickedTotal.toFixed(2))}</strong> of{' '}
                {formatCurrency(amt.toFixed(2))}
                {pickedPayables.length > 0 && !payablesBalance && (
                  <span className="field-error" style={{ marginLeft: 8, fontSize: 12 }}>
                    off by {formatCurrency(Math.abs(pickedTotal - amt).toFixed(2))}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn-primary"
                // Enabled only on an exact match: a settlement that does not
                // equal the payment would leave the bank line half-explained.
                disabled={!payablesBalance || settlePayables.isPending}
                onClick={() =>
                  settlePayables.mutate(
                    { transactionId: tx.id, journalLineIds: pickedPayables },
                    { onSuccess: () => onClose() },
                  )
                }
              >
                {settlePayables.isPending
                  ? 'Settling…'
                  : `Settle ${pickedPayables.length || ''} item${pickedPayables.length === 1 ? '' : 's'}`.trim()}
              </button>
            </div>
            {settlePayables.error instanceof ApiError && (
              <div className="field-error" style={{ fontSize: 12, marginTop: 6 }}>
                {(settlePayables.error.body as { error?: string } | null)?.error ??
                  'Could not settle those items'}
              </div>
            )}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (intercompany) {
              if (!otherCompanyId || !otherAccountId) return;
              matchIc.mutate(
                {
                  transactionId: tx.id,
                  input: { otherCompanyId, otherAccountId, description: memo.trim() || undefined },
                },
                { onSuccess: () => onClose() },
              );
              return;
            }
            if (splitMode) {
              if (!splitValid) return;
              match.mutate(
                {
                  transactionId: tx.id,
                  input: {
                    splits: splits.map((r) => ({ accountId: r.accountId, amount: String(parseFloat(r.amount) || 0) })),
                    memo: memo.trim() || undefined,
                  },
                },
                { onSuccess: () => onClose() },
              );
              return;
            }
            if (!accountId) return;
            // A processor fee turns a simple match into a 3-line entry: the
            // offset account absorbs the GROSS (what the customer paid), the
            // fee account takes the withheld cut, and the two net to what the
            // bank actually moved. Sent as splits — the fee is the negative leg.
            if (feeOn && feeValid) {
              match.mutate(
                {
                  transactionId: tx.id,
                  input: {
                    splits: [
                      { accountId, amount: grossAmount.toFixed(2) },
                      { accountId: feeAcct, amount: (-feeNum).toFixed(2) },
                    ],
                    contactId: contactId || undefined,
                    locationId: locationId || undefined,
                    classId: classId || undefined,
                    memo: memo.trim() || undefined,
                  },
                },
                { onSuccess: () => onClose() },
              );
              return;
            }
            match.mutate(
              {
                transactionId: tx.id,
                input: {
                  accountId,
                  contactId: contactId || undefined,
                  locationId: locationId || undefined,
                  classId: classId || undefined,
                  memo: memo.trim() || undefined,
                },
              },
              { onSuccess: () => onClose() },
            );
          }}
        >
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={intercompany}
                onChange={(e) => {
                  setIntercompany(e.target.checked);
                  if (e.target.checked) setSplitMode(false);
                }}
              />
              Intercompany
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={splitMode}
                onChange={(e) => {
                  setSplitMode(e.target.checked);
                  if (e.target.checked) setIntercompany(false);
                }}
              />
              Split across accounts
            </label>
          </div>

          {splitMode && (
            <div style={{ marginBottom: 8 }}>
              {splits.map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <AccountSelect
                      companyId={companyId}
                      accounts={accounts}
                      value={row.accountId}
                      onChange={(id) => setSplits((p) => p.map((r, j) => (j === i ? { ...r, accountId: id } : r)))}
                    />
                  </div>
                  <input
                    inputMode="decimal"
                    value={row.amount}
                    onChange={(e) => setSplits((p) => p.map((r, j) => (j === i ? { ...r, amount: e.target.value } : r)))}
                    placeholder="0.00"
                    style={{ width: 110, textAlign: 'right' }}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setSplits((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : p))}
                    title="Remove line"
                  >
                    ×
                  </button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-secondary" onClick={() => setSplits((p) => [...p, { accountId: '', amount: '' }])}>
                    + Add line
                  </button>
                  {/* A fee withheld from the deposit goes on a negative line: the
                      gross receivable plus the negative fee net to what the bank
                      actually received. Pre-fills the shortfall so the common
                      Stripe case is one click. */}
                  <button
                    type="button"
                    className="btn-secondary"
                    title="Add a negative line for a processor fee withheld from this deposit"
                    onClick={() =>
                      setSplits((p) => [
                        ...p,
                        {
                          accountId: feeAccountId,
                          amount: splitTotal > amt ? (amt - splitTotal).toFixed(2) : '',
                        },
                      ])
                    }
                  >
                    + Fee line
                  </button>
                </div>
                <div className={Math.abs(splitTotal - amt) < 0.005 ? 'muted' : 'field-error'} style={{ fontSize: 13 }}>
                  {formatCurrency(splitTotal.toFixed(2))} of {formatCurrency(amt.toFixed(2))}
                  {Math.abs(splitTotal - amt) >= 0.005 && ` · ${formatCurrency((amt - splitTotal).toFixed(2))} left`}
                </div>
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                Lines must net to {formatCurrency(amt.toFixed(2))}. Enter a fee withheld from the
                deposit as a negative amount — e.g. {formatCurrency('200.00')} to A/R plus{' '}
                {formatCurrency('-6.30')} in processing fees.
              </p>
              <label htmlFor="m-split-memo" style={{ display: 'block', fontSize: 13, marginTop: 8 }}>Memo</label>
              <input id="m-split-memo" value={memo} onChange={(e) => setMemo(e.target.value)} style={{ width: '100%' }} />
            </div>
          )}

          {intercompany && (
            <div className="form-grid">
              <label htmlFor="m-otherco">Other company *</label>
              <select
                id="m-otherco"
                value={otherCompanyId}
                onChange={(e) => {
                  setOtherCompanyId(e.target.value);
                  setOtherAccountId('');
                }}
                required
              >
                <option value="">— select —</option>
                {otherCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <label htmlFor="m-otheracct">Account on their side *</label>
              <select
                id="m-otheracct"
                value={otherAccountId}
                onChange={(e) => setOtherAccountId(e.target.value)}
                required
                disabled={!otherCompanyId}
              >
                <option value="">— select —</option>
                {otherPostable.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </select>

              <label htmlFor="m-ic-memo">Memo</label>
              <input id="m-ic-memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
            </div>
          )}

          {intercompany && otherCompanyId && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {isOut
                ? `This company funds ${otherName}: DR Intercompany Receivable / CR this bank here; ${otherName} gets DR the account above / CR Intercompany Payable.`
                : `${otherName} funded this company: that side DR Intercompany Receivable / CR the account above; here DR this bank / CR Intercompany Payable.`}
            </p>
          )}

          <div className="form-grid" style={intercompany || splitMode ? { display: 'none' } : undefined}>
            <label htmlFor="m-acct">Offset account *</label>
            <AccountSelect
              id="m-acct"
              companyId={companyId}
              accounts={accounts}
              value={accountId}
              onChange={setAccountId}
              required={!intercompany && !splitMode}
            />

            <label htmlFor="m-fee-on">Processor fee</label>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input
                  id="m-fee-on"
                  type="checkbox"
                  checked={feeOn}
                  onChange={(e) => setFeeOn(e.target.checked)}
                />
                A fee was withheld from this {isOut ? 'payment' : 'deposit'}
              </label>
              {feeOn && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      inputMode="decimal"
                      value={feeAmount}
                      onChange={(e) => setFeeAmount(e.target.value)}
                      placeholder="6.30"
                      aria-label="Fee amount"
                      style={{ width: 100, textAlign: 'right' }}
                    />
                    <div style={{ flex: 1 }}>
                      <AccountSelect
                        companyId={companyId}
                        accounts={accounts}
                        value={feeAcct}
                        onChange={setFeeAcct}
                      />
                    </div>
                  </div>
                  {/* Show the resulting entry outright — the whole point is that
                      the offset clears at GROSS while the bank keeps the net. */}
                  {feeValid ? (
                    <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                      Clears {formatCurrency(grossAmount.toFixed(2))} against the offset account,
                      posts {formatCurrency(feeNum.toFixed(2))} to fees, and leaves{' '}
                      {formatCurrency(amt.toFixed(2))} on the bank.
                    </p>
                  ) : (
                    <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                      Enter the fee amount and the account it posts to.
                    </p>
                  )}
                </div>
              )}
            </div>

            {contacts.length > 0 && (
              <>
                <label htmlFor="m-contact">Contact</label>
                <select
                  id="m-contact"
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value)}
                >
                  <option value="">— none —</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </>
            )}

            {locations.length > 0 && (
              <>
                <label htmlFor="m-loc">Location</label>
                <select
                  id="m-loc"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  <option value="">— none —</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </>
            )}

            {classes.length > 0 && (
              <>
                <label htmlFor="m-cls">Class</label>
                <select
                  id="m-cls"
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                >
                  <option value="">— none —</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </>
            )}

            <label htmlFor="m-memo">Memo</label>
            <input
              id="m-memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </div>

          {errorMessage && (
            <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              type="submit"
              className="btn-primary"
              disabled={
                intercompany
                  ? !otherCompanyId || !otherAccountId || matchIc.isPending
                  : splitMode
                    ? !splitValid || match.isPending
                    : !accountId || (feeOn && !feeValid) || match.isPending
              }
            >
              {(intercompany ? matchIc.isPending : match.isPending)
                ? 'Posting…'
                : intercompany
                  ? 'Post intercompany'
                  : 'Categorize → post to GL'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
